<template>
  <div class="content markdown-content" ref="container" />
  <!-- TODO find a way without aditionnal element
    <div style="clear:both"/> -->
</template>

<script setup lang="ts">
import type { IconLookup } from '@fortawesome/fontawesome-svg-core';
import { onBeforeUnmount, onMounted, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

import { useGlobalProperties } from '@/js/composables/global-properties';
import config from '@/js/config';
import iconSvg from '@/js/icon-svg';
import { getEmbeddedImageUrl, getThumbnailSize, type ImageSize } from '@/js/image-urls';
import { sanitizeRichHtml } from '@/js/sanitize-html';
import type { ViewerImage } from '@/js/vue-plugins/image-viewer';

// copied from vue router : https://github.com/vuejs/vue-router/blob/dev/src/components/link.js
function guardEvent(e: MouseEvent) {
  // don't redirect with control keys
  if (e.metaKey || e.altKey || e.ctrlKey || e.shiftKey) return;
  // don't redirect when preventDefault called
  if (e.defaultPrevented) return;
  // don't redirect on right click
  if (e.button !== undefined && e.button !== 0) return;
  // don't redirect if `target="_blank"`
  const currentTarget = e.currentTarget as Element | null;
  if (currentTarget && currentTarget.getAttribute) {
    const target = currentTarget.getAttribute('target');
    if (/\b_blank\b/i.test(target ?? '')) return;
  }
  // this may be a Weex event which doesn't have this method
  if (e.preventDefault) {
    e.preventDefault();
  }
  return true;
}

const getFontAwesomeSrc = function (prefix: string, iconeName: string) {
  let svgSource = iconSvg({ prefix, iconName: iconeName } as IconLookup);
  svgSource = svgSource.replace('fill="currentColor"', 'fill="#F93"');
  return 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svgSource);
};

const getEmojiSrc = function (emojiSource: string, svgName: string) {
  if (emojiSource === 'emojione') {
    return `https://cdn.jsdelivr.net/emojione/assets/svg/${svgName}.svg`;
  }

  if (emojiSource === 'c2c-activities') {
    return getFontAwesomeSrc('activity', svgName);
  }

  if (emojiSource === 'c2c-waypoints') {
    return getFontAwesomeSrc('waypoint', svgName);
  }
};

// an image of the content, with the image document it opens in the image viewer
type EmbeddedImage = HTMLImageElement & { c2cExtrapoledDocument: ViewerImage & { type: string } };

const props = defineProps({
  content: {
    type: String,
    required: true,
  },
});

const router = useRouter();
const { $imageViewer, $language } = useGlobalProperties();

const containerEl = useTemplateRef<HTMLDivElement>('container');

// lazy images that are not loaded yet would be missing in the printout
function loadAllImages() {
  for (const image of containerEl.value!.querySelectorAll<HTMLImageElement>('img[loading=lazy]')) {
    image.loading = 'eager';
  }
}

function compute() {
  const container = containerEl.value!;

  const addClasses = function (cssSelector: string, classes: string[]) {
    const nodes = container.querySelectorAll(cssSelector);

    for (const node of nodes) {
      node.classList.add(...classes);
    }
  };

  // props.content comes from wiki articles converted from user-authored markdown:
  // sanitize it before injecting it in the DOM to prevent stored XSS.
  container.innerHTML = sanitizeRichHtml(props.content);

  if (props.content.includes('c2c:role')) {
    computeFigures(container.querySelectorAll('figure[c2c\\:role=embedded-figure]'));
    computeImages(container.querySelectorAll<EmbeddedImage>('img[c2c\\:role=embedded-image]'));
    computeEmojis(container.querySelectorAll<HTMLImageElement>('img[c2c\\:role=emoji]'));
    computeAnchors(container.querySelectorAll<HTMLAnchorElement>('a[c2c\\:role=internal-link]'));
    computeVideos(container.querySelectorAll('div[c2c\\:role=video] > iframe'));

    addClasses('div[c2c\\:role=info]', ['notification', 'is-info']);
    addClasses('div[c2c\\:role=warning]', ['notification', 'is-warning']);
    addClasses('div[c2c\\:role=danger]', ['notification', 'is-danger']);
    addClasses('div[c2c\\:role=video]', ['no-print']);
    addClasses('table[c2c\\:role=ltag]', ['table']);
  }
}

function computeVideos(iframes: Iterable<Element>) {
  for (const iframe of iframes) {
    iframe.setAttribute('allowfullscreen', 'true');
    // Restrict what the embedded video player (YouTube/Vimeo/Dailymotion) iframe
    // can do: allow-scripts and allow-same-origin are required by these players'
    // own embed code, allow-presentation is required for fullscreen playback.
    // Notably missing: allow-top-navigation, allow-popups, allow-forms...
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-presentation');
  }
}

function computeEmojis(emojis: Iterable<HTMLImageElement>) {
  for (const emoji of emojis) {
    const emojiSource = emoji.attributes.getNamedItem('c2c:emoji-db')!.value;
    const svgName = emoji.attributes.getNamedItem('c2c:svg-name')!.value;
    emoji.src = getEmojiSrc(emojiSource, svgName)!; // `${svgCdns[emojiSource]}${svgName}.svg`
  }
}

function computeFigures(figures: Iterable<Element>) {
  for (const figure of figures) {
    const image = figure.children[0];
    const size = image.attributes.getNamedItem('c2c:size');

    if (size) {
      figure.setAttribute('c2c:size', size.value);
    }
  }
}

/**
 * Gives an embedded image the size it will have, from the size of its original (c2c:width and c2c:height, given by the
 * API since v6_api #2579): the page keeps its space while it loads, instead of shifting (#4883). False when unknown.
 */
function reserveImageSpace(image: HTMLImageElement): boolean {
  const original = { width: Number(image.getAttribute('c2c:width')), height: Number(image.getAttribute('c2c:height')) };
  // positive integers only: raw HTML in the text may carry these attributes too, with any value
  const isSize = (size: { width: number; height: number } | undefined): size is { width: number; height: number } =>
    !!size && [size.width, size.height].every((value) => Number.isSafeInteger(value) && value > 0);
  if (!isSize(original)) {
    return false;
  }
  const size = image.getAttribute('c2c:size') as ImageSize | '' | null;
  // SI is cropped to 200x200, no size is the original (v6_images)
  const shown = size === 'SI' ? { width: 200, height: 200 } : size ? getThumbnailSize(original, size) : original;
  // a very thin image may round to 0 px
  if (!isSize(shown)) {
    return false;
  }
  // the bounds of the CSS (max-width: 100%, height: auto) keep the ratio
  image.width = shown.width;
  image.height = shown.height;
  if (size === 'MI') {
    // a medium image fits in min(400px, 25vw) both ways (styles below): bound its longer side, the other one follows
    // the ratio (bounding both would squash it)
    const bound = `min(${Math.max(shown.width, shown.height)}px, 400px, 25vw)`;
    image.style.aspectRatio = `${shown.width} / ${shown.height}`;
    if (shown.width >= shown.height) {
      image.style.width = bound;
      image.style.height = 'auto';
    } else {
      image.style.height = bound;
      image.style.width = 'auto';
    }
  }
  return true;
}

function computeImages(images: Iterable<EmbeddedImage>) {
  for (const image of images) {
    const document_id = parseInt(image.attributes.getNamedItem('c2c:document-id')!.value, 10);
    image.c2cExtrapoledDocument = {
      document_id,
      locales: [{ title: '...' }],
      available_langs: [$language.current!],
      type: 'i',
    };

    image.addEventListener('click', () => {
      $imageViewer.show(image.c2cExtrapoledDocument);
    });

    const urls = {
      url: image.getAttribute('c2c:url'),
      urlProxy: image.getAttribute('c2c:url-proxy')!,
    };
    const parent = image.parentNode!;
    const picture = document.createElement('picture');

    // Until all images are migrated only images uploaded after a given timestamp
    // or with an id greater than a given one have their webp and avif versions.
    // Original images (no size) have no modern versions.
    if (
      config.urls.modernThumbnailsId &&
      document_id > config.urls.modernThumbnailsId &&
      image.getAttribute('c2c:size')
    ) {
      const avif = document.createElement('source');
      avif.setAttribute('type', 'image/avif');
      avif.setAttribute('srcset', getEmbeddedImageUrl(urls, 'avif'));
      const webp = document.createElement('source');
      webp.setAttribute('type', 'image/webp');
      webp.setAttribute('srcset', getEmbeddedImageUrl(urls, 'webp'));
      picture.appendChild(avif);
      picture.appendChild(webp);
    }
    picture.appendChild(image);
    parent.appendChild(picture);

    // Some documents embed hundreds of images (each one being a request to the API
    // image proxy when the direct media url is not known): only load them when they get
    // close to the viewport.
    // loading must be set before src, and the image needs a size while it is loading,
    // otherwise all the 0x0 images are considered in the viewport and loaded at once:
    // its own, when known, else a placeholder size (is-loading)
    if (!reserveImageSpace(image)) {
      image.classList.add('is-loading');
    }
    const onLoaded = () => image.classList.remove('is-loading');
    image.addEventListener('load', onLoaded, { once: true });
    image.addEventListener('error', onLoaded, { once: true });
    image.loading = 'lazy';
    image.decoding = 'async';
    image.src = getEmbeddedImageUrl(urls);

    $imageViewer.push(image.c2cExtrapoledDocument);
  }
}

function computeAnchors(anchors: Iterable<HTMLAnchorElement>) {
  for (const anchor of anchors) {
    const attribute = (name: string) => anchor.attributes.getNamedItem(name)?.value;

    const { fullPath, href } = router.resolve({
      name: attribute('c2c:document-type')!.slice(0, -1),
      params: {
        id: parseInt(attribute('c2c:document-id')!, 10),
        lang: attribute('c2c:lang'),
        title: attribute('c2c:slug'),
      },
      hash: attribute('c2c:anchor') ? '#' + attribute('c2c:anchor') : undefined,
    });

    anchor.href = href;
    anchor.addEventListener('click', (e) => {
      if (guardEvent(e)) {
        router.push(fullPath);
      }
    });
  }
}

watch(() => props.content, compute);

onMounted(() => {
  compute();
  window.addEventListener('beforeprint', loadAllImages);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeprint', loadAllImages);
});
</script>

<style lang="scss">
// Not scoped syle, because CSS selector are not explicitly present in template
.markdown-content {
  &:not(:last-child) {
    margin-bottom: 1.5rem;
  }

  h3 {
    font-size: 1.5rem !important;
    margin-bottom: 0.5em !important;
    border-bottom: 1px solid #eee;
  }
  h4 {
    font-size: 1.1rem !important;
    margin-bottom: 0.25em !important;
  }
  h5 {
    font-size: 1rem !important;
    margin-bottom: 0.25em !important;
  }
  hr {
    margin: 1rem 0 !important;
  }
}

span[c2c\:role='header-emphasis'] {
  font-size: 80%;
  font-style: italic;
}

img[c2c\:role='emoji'] {
  width: 1.43em;
  height: 1.43em;
  vertical-align: bottom;
}

figure[c2c\:role='embedded-figure'] {
  border-radius: 4px;
  border: 1px solid #ddd;
  padding: 0.5rem;
  background: #fafafa;
  max-width: 100%;
  display: table;

  img {
    cursor: pointer;
  }

  // placeholder size while the lazy image is not loaded
  img.is-loading {
    min-width: 100px;
    min-height: 100px;
  }
}

// small image
// SI is a 200*200 image, no need to specify anything
// figure[c2c\:size=SI]{

// }

// medium image
figure[c2c\:size='MI'] {
  max-width: min(400px, 25vw);
  max-height: min(400px, 25vw);

  img {
    max-width: min(400px, 25vw);
    max-height: min(400px, 25vw);
  }
}

// big image
// figure[c2c\:size=BI]{

// }

// original size image
// TODO MARKDOWN PARSER HAS TO EXPOSE THIS EXPLICTLY
// figure[c2c\:size=OI]{

// }

figure[c2c\:position='right'] {
  float: right;
  clear: right;
  margin-right: 0 !important;
  z-index: 1;
  position: relative;
}

figure[c2c\:position='left'] {
  float: left;
  clear: left;
  margin-left: 0 !important;
  z-index: 1;
  position: relative;
}

figure[c2c\:position='center'] {
  margin-left: auto !important;
  margin-right: auto !important;
  clear: both;
}

figure[c2c\:position='inline'] {
  margin-right: 0 !important;
  margin-left: 0 !important;
  // well, no :(
  // display: inline-block;
}

table[c2c\:role='ltag'] {
  width: auto !important; // on prod, bulma CSS is after this CSS ??

  .pitch {
    font-weight: bold;
  }
}

div[c2c\:role='info'],
div[c2c\:role='warning'],
div[c2c\:role='danger'] {
  overflow: hidden;
}

div[c2c\:role='video'] {
  text-align: center;

  iframe {
    width: 400px;
    height: 250px;
    max-width: 100%;
    @media screen and (min-width: $tablet) {
      width: 420px;
      height: 315px;
    }
  }
}

@media screen and (max-width: $tablet) {
  table[c2c\:role='ltag'] {
    tr {
      display: flex;
      flex-flow: row wrap;
      padding: 0.4em 0 0.5em;

      &:not(:last-child) {
        border-bottom: 1px solid #d0d0d0;
      }
    }

    td,
    th {
      border: 0;
      padding-top: 0;
      padding-bottom: 0;
    }
  }
}

@media print {
  .markdown-content {
    &:not(:last-child) {
      margin-bottom: 0.5rem !important;
    }

    h3 {
      font-size: 1.2rem !important;
      margin-bottom: 0.25em !important;
    }
  }
}
</style>
